define((require) => {
    "use strict";

    var _              = require("underscore"),
        EditorEvents   = require("shared/services/EditorEvents"),
        Marionette     = require("marionette"),
        Preconditions  = require("shared/util/Preconditions");

    return Marionette.ItemView.extend("AbstractGuidelineView", {
        _newPosition: null,
        _originalClientXY: null,
        _originalPosition: null,
        _scaledExperienceWidthHeight: null,
        _tooltipElement: null,
        _viewModel: null,
        _zoomModel: null,
        _outsideSceneThreshold: 30, // How many pixels a guideline can be dragged out of the scene before deletion

        initialize: function(options) {
            this._viewModel = options.viewModel;
            this._zoomModel = this._viewModel.get("zoomModel");
            this._tooltipElement = options.tooltipElement;
            _.bindAll(this, "_onMouseMove", "_onMouseUp");
        },

        events: {
            "mousedown": "_onMouseDown"
        },

        guidelineAdded: function(mouseDownEvent) {
            var cursorPosition = this._getClientXY(mouseDownEvent);
            var scenePosition = Math.round(this.parentView.el.getBoundingClientRect()[this._getTopLeft()]);
            var startPosition = Math.round((cursorPosition - scenePosition) / this._zoomModel.get("scale"));
            this.model.set("position", startPosition);

            // Render the guideline to move it to the position set on the line above.
            this.render();
            this._onMouseDown(mouseDownEvent);

            this._triggerEditorEvent(EditorEvents.constants.ADD_GUIDELINE, {triggerOrigin: "ruler"});
        },

        onRender: function() {
            var position = this.model.get("position");
            this._placeGuideline(position);
        },

        // Used for registering mix panel events
        _triggerEditorEvent: function(name, eventProperties) {
            var experience = this._viewModel.get("experience");
            var project = this._viewModel.get("project");

            eventProperties = _.defaults(eventProperties, {
                experienceId: experience.get("id"),
                experienceVersion: experience.get("version"),
                experiencePushedVersion: experience.get("pushedVersion"),
                direction: this._getDirection(),
                triggerOrigin: eventProperties.triggerOrigin
            });

            if (project) {
                eventProperties.projectId = project.get("id");
            }

            EditorEvents.trigger(name, eventProperties);
        },

        _isOutsideScene: function(scaledExperienceWidthHeight, unscaledPosition) {
            var scaledPosition = unscaledPosition * this._zoomModel.get("scale");
            var scaledOffset = this.parentView.getViewPortOffset(this._getDirection());
            var isOutside = (scaledPosition - scaledOffset < 0 || scaledPosition >= scaledExperienceWidthHeight);
            return isOutside;
        },

        _isOutsideSceneThreshold: function(scaledExperienceWidthHeight, unscaledPosition) {
            var scaledPosition = unscaledPosition * this._zoomModel.get("scale");
            var threshold = this._outsideSceneThreshold * this._zoomModel.get("scale");
            return ((scaledPosition < threshold * -1) || (scaledPosition > (scaledExperienceWidthHeight + threshold)));
        },

        _onMouseDown: function(e) {
            e.preventDefault();
            this.$el.addClass("guideline-dragging");

            this._originalClientXY = this._getClientXY(e);
            this._originalPosition = this.model.get("position");

            // Make sure newPosition has a value in onMouseUp even if the cursor wasn't moved.
            // This happens when the user clicks on a guideline or a ruler, without dragging.
            this._newPosition = this._originalPosition;
            this._scaledExperienceWidthHeight = this._zoomModel.get("scale") * this._viewModel.get("experience").get(this._getWidthHeight());

            this._tooltipElement.removeClass("hidden");

            this._updateTooltip(e, this._newPosition, this._scaledExperienceWidthHeight);

            // Capture the mouse events globally, because when dragging a guideline we don't want to
            // interact with anything else in the meantime.
            document.body.addEventListener("mousemove", this._onMouseMove);
            document.body.addEventListener("mouseup", this._onMouseUp);
        },

        _onMouseMove: function(e) {
            var cursorMovement = (this._getClientXY(e) - this._originalClientXY) / this._zoomModel.get("scale");
            this._newPosition = Math.round((this._originalPosition + cursorMovement));

            if (this._isOutsideScene(this._scaledExperienceWidthHeight, this._newPosition)) {
                this.$el.addClass("guideline-outside");
                if (this._isOutsideSceneThreshold(this._scaledExperienceWidthHeight, this._newPosition)) {
                    this._onMouseUp();
                }
            } else {
                this.$el.removeClass("guideline-outside");
            }

            this._placeGuideline(this._newPosition);
            this._updateTooltip(e, this._newPosition, this._scaledExperienceWidthHeight);
        },

        _onMouseUp: function() {
            document.body.removeEventListener("mousemove", this._onMouseMove);
            document.body.removeEventListener("mouseup", this._onMouseUp);

            this.$el.removeClass("guideline-dragging");

            if (this._isOutsideScene(this._scaledExperienceWidthHeight, this._newPosition)) {
                this.model.collection.remove(this.model);
                this._triggerEditorEvent(EditorEvents.constants.DELETE_GUIDELINE, {triggerOrigin: "guideline"});
            } else {
                // Rounding a second time just to make sure we don't get a floating point error,
                // see http://stackoverflow.com/q/1458633/37147.
                var newPosition = Math.round(this._newPosition);
                this.model.set("position", newPosition);
            }

            this._tooltipElement.addClass("hidden");
            this.parentView.trigger("guideline:moved");
        },

        _placeGuideline: function(position) {
            var css = {};
            css[this._getTopLeft()] = position + "px";
            this.$el.css(css);
        },

        _updateTooltip: function(e, newPosition, scaledExperienceWidthHeight) {
            this._tooltipElement.toggleClass("hidden", this._isOutsideScene(scaledExperienceWidthHeight, newPosition));

            var parentOffset = this.parentView.$el.offset();
            var tipText = this._getTooltipLabel() + newPosition + " px";

            var position = {
                left: Math.round((e.clientX - parentOffset.left + 10) / this._zoomModel.get("scale")) + "px",
                top: Math.round((e.clientY - parentOffset.top + 10) / this._zoomModel.get("scale")) + "px"
            };

            this._tooltipElement.css(position).text(tipText);
        },

        /**
         * Return e.clientX or e.clientY, depending on this being a horizontal or a vertical
         * guideline.
         * @param {jQuery.Event} e
         */
        // eslint-disable-next-line no-unused-vars
        _getClientXY: function(e) {
            Preconditions.abstractMethodError();
        },

        /**
         * Returns "HORIZONTAL" or "VERTICAL", depending on this being a horizontal or a vertical
         * guideline.
         */
        _getDirection: function() {
            Preconditions.abstractMethodError();
        },

        /**
         * Return the label to prefix the tooltop with, "x: ", or "y: ", depending on this being a
         * horizontal or vertical guideline.
         */
        _getTooltipLabel: function() {
            Preconditions.abstractMethodError();
        },

        /**
         * Return "top" or "left", depending on this being a horizontal or a vertical guideline.
         */
        _getTopLeft: function() {
            Preconditions.abstractMethodError();
        },

        /**
         * Return "width" or "height", depending on this being a horizontal or a vertical
         * guideline.
         */
        _getWidthHeight: function() {
            Preconditions.abstractMethodError();
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/editor/guideline/AbstractGuidelineView.js